// Flow Header Toolbar (PRD §1.9, WP-08).
// Flow name editor, type badge, Trigger/Settings modal launcher, validation badge, export, save draft, and publish buttons.
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../../lib/i18n';
import type { FlowType, FlowValidationIssue } from '../../lib/flows';
import FlowValidationBadge from './FlowValidationBadge';
import { Button } from '../ui/Button';
import { ChevronLeftIcon, SettingsIcon, DownloadIcon } from '../icons';

interface FlowHeaderProps {
  name: string;
  type: FlowType;
  version?: number;
  published?: boolean;
  validationIssues: FlowValidationIssue[];
  saving: boolean;
  publishing: boolean;
  onNameChange: (name: string) => void;
  onOpenSettings: () => void;
  onExport: () => void;
  onSaveDraft: () => void;
  onPublish: () => void;
}

export default function FlowHeader({
  name,
  type,
  version = 1,
  published = false,
  validationIssues,
  saving,
  publishing,
  onNameChange,
  onOpenSettings,
  onExport,
  onSaveDraft,
  onPublish,
}: FlowHeaderProps) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [localName, setLocalName] = useState(name);
  const [isEditingName, setIsEditingName] = useState(false);

  useEffect(() => {
    setLocalName(name);
  }, [name]);

  const handleBlurName = () => {
    setIsEditingName(false);
    if (localName.trim() && localName !== name) {
      onNameChange(localName.trim());
    } else {
      setLocalName(name);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleBlurName();
    } else if (e.key === 'Escape') {
      setLocalName(name);
      setIsEditingName(false);
    }
  };

  const typeColorMap: Record<FlowType, { bg: string; text: string }> = {
    keyword: { bg: 'bg-blue-50 dark:bg-blue-900/30', text: 'text-blue-600 dark:text-blue-400' },
    system: { bg: 'bg-purple-50 dark:bg-purple-900/30', text: 'text-purple-600 dark:text-purple-400' },
    sequence: { bg: 'bg-amber-50 dark:bg-amber-900/30', text: 'text-amber-600 dark:text-amber-400' },
  };

  const typeConfig = typeColorMap[type] || typeColorMap.keyword;

  const errors = validationIssues.filter((i) => i.level === 'error').map((i) => i.message);
  const warnings = validationIssues.filter((i) => i.level === 'warning').map((i) => i.message);

  return (
    <header className="flex flex-wrap items-center justify-between gap-3 border-b border-line bg-surface px-4 py-2.5 dark:border-slate-800 dark:bg-slate-900">
      {/* Left side: Back button, Name, Type & Status badge */}
      <div className="flex items-center gap-3 min-w-0">
        <button
          type="button"
          onClick={() => navigate('/flows')}
          className="flex h-9 w-9 items-center justify-center rounded-lg border border-line bg-surface text-ink-2 hover:bg-slate-100 hover:text-ink dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700 rtl:rotate-180"
          title={t('flow.header.backToFlows')}
        >
          <ChevronLeftIcon className="h-5 w-5 rtl:rotate-180" />
        </button>

        <div className="flex items-center gap-2 min-w-0">
          {isEditingName ? (
            <input
              type="text"
              value={localName}
              onChange={(e) => setLocalName(e.target.value)}
              onBlur={handleBlurName}
              onKeyDown={handleKeyDown}
              autoFocus
              className="rounded-lg border border-brand bg-white px-2.5 py-1 text-sm font-bold text-ink outline-none dark:border-brand dark:bg-slate-800 dark:text-white"
            />
          ) : (
            <div
              onClick={() => setIsEditingName(true)}
              className="cursor-pointer rounded-lg px-2 py-1 text-sm font-bold text-ink hover:bg-slate-100 dark:text-white dark:hover:bg-slate-800 truncate max-w-[200px] sm:max-w-[320px]"
              title={t('flow.header.clickToRename')}
            >
              {name || t('flow.header.untitledFlow')}
            </div>
          )}

          {/* Flow Type Badge */}
          <span
            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold uppercase tracking-wider ${typeConfig.bg} ${typeConfig.text}`}
          >
            {type}
          </span>

          {/* Published vs Draft Badge */}
          {published ? (
            <span className="inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400">
              v{version} • {t('flow.header.published')}
            </span>
          ) : (
            <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-ink-3 dark:bg-slate-800 dark:text-slate-400">
              v{version} • {t('flow.header.draft')}
            </span>
          )}
        </div>
      </div>

      {/* Right side: Actions & Controls */}
      <div className="flex items-center gap-2">
        {/* Validation indicator */}
        <FlowValidationBadge valid={errors.length === 0} errors={errors} warnings={warnings} />

        {/* Trigger / Settings */}
        <button
          type="button"
          onClick={onOpenSettings}
          className="flex items-center gap-1.5 rounded-lg border border-line bg-surface px-3 py-1.5 text-xs font-semibold text-ink-2 hover:bg-slate-100 hover:text-ink dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
        >
          <SettingsIcon className="h-4 w-4 text-ink-3 dark:text-slate-400" />
          <span>{t('flow.header.triggerAndSettings')}</span>
        </button>

        {/* Export JSON */}
        <button
          type="button"
          onClick={onExport}
          className="hidden sm:flex items-center gap-1.5 rounded-lg border border-line bg-surface px-3 py-1.5 text-xs font-semibold text-ink-2 hover:bg-slate-100 hover:text-ink dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
          title={t('flow.header.exportJson')}
        >
          <DownloadIcon className="h-4 w-4 text-ink-3 dark:text-slate-400" />
          <span>{t('flow.header.export')}</span>
        </button>

        {/* Save Draft */}
        <Button
          variant="secondary"
          size="sm"
          onClick={onSaveDraft}
          disabled={saving || publishing}
          className="text-xs font-semibold"
        >
          {saving ? t('flow.header.saving') : t('flow.header.saveDraft')}
        </Button>

        {/* Publish Changes */}
        <Button
          variant="primary"
          size="sm"
          onClick={onPublish}
          disabled={saving || publishing}
          className="text-xs font-semibold shadow-sm"
        >
          {publishing ? t('flow.header.publishing') : t('flow.header.publish')}
        </Button>
      </div>
    </header>
  );
}
